// Type helpers to prevent common Floating UI mistakes
import { useState } from 'react';
import {
  type UseFloatingOptions,
  type UseFloatingReturn,
  useFloating as useFloatingOriginal,
} from '@floating-ui/react';

/**
 * Common mistake: Forgetting to manage open state with Floating UI
 *
 * This type helper ensures you always provide both open and onOpenChange
 * to useFloating, preventing tooltips that never appear.
 */
type RequiredFloatingState = {
  /** Whether the floating element is open - REQUIRED */
  open: boolean;
  /** Callback when open state changes - REQUIRED */
  onOpenChange: (open: boolean) => void;
};

type SafeUseFloatingOptions = UseFloatingOptions & RequiredFloatingState;

/**
 * Type-safe wrapper around useFloating that requires open state management
 *
 * Prevents the common mistake of forgetting open/onOpenChange which causes
 * tooltips that never appear because the interaction hooks can't manage state.
 *
 * @example
 * ```tsx
 * // ❌ This won't work (tooltip never appears):
 * const { context } = useFloating({ placement: 'top' });
 *
 * // ✅ This forces you to do it right:
 * const [isOpen, setIsOpen] = useState(false);
 * const { context } = useSafeFloating({
 *   open: isOpen,
 *   onOpenChange: setIsOpen,
 *   placement: 'top'
 * });
 * ```
 */
export const useSafeFloating = (
  options: SafeUseFloatingOptions
): UseFloatingReturn => useFloatingOriginal(options);

/**
 * Hook that provides the correct state management pattern for Floating UI
 *
 * Returns the state and the options you need to pass to useFloating
 *
 * @example
 * ```tsx
 * const { isOpen, floatingOptions } = useFloatingState({
 *   placement: 'top',
 *   middleware: [offset(5), flip(), shift()],
 * });
 *
 * const { refs, floatingStyles, context } = useFloating(floatingOptions);
 * ```
 */
export const useFloatingState = (
  baseOptions: Omit<UseFloatingOptions, 'open' | 'onOpenChange'> = {}
) => {
  const [isOpen, setIsOpen] = useState(false);

  const floatingOptions: SafeUseFloatingOptions = {
    ...baseOptions,
    open: isOpen,
    onOpenChange: setIsOpen,
  };

  return {
    isOpen,
    setIsOpen,
    floatingOptions,
  };
};

/**
 * Type guard to ensure useFloating is called with required state management
 *
 * Use this in code reviews or linting rules to catch missing state management
 */
export type FloatingUIStateCheck<T> = T extends {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
  ? T
  : 'ERROR: useFloating requires open and onOpenChange for proper state management';

/**
 * Utility type to extract common useFloating mistakes at compile time
 *
 * Usage in component props:
 * type MyComponentProps = {
 *   floatingConfig: ValidFloatingConfig;
 * }
 */
export type ValidFloatingConfig = RequiredFloatingState & {
  // Force developers to think about these common requirements
  placement?: UseFloatingOptions['placement'];
  middleware?: UseFloatingOptions['middleware'];
  whileElementsMounted?: UseFloatingOptions['whileElementsMounted'];
};

/**
 * Template pattern for creating new Floating UI components
 * Copy this pattern to avoid common mistakes
 *
 * @example
 * ```tsx
 * const MyTooltip = () => {
 *   // 1. Always start with state management
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   // 2. Always provide open + onOpenChange
 *   const { refs, floatingStyles, context } = useFloating({
 *     open: isOpen,
 *     onOpenChange: setIsOpen,
 *     // ... other options
 *   });
 *
 *   // 3. Setup interactions
 *   const hover = useHover(context);
 *   const dismiss = useDismiss(context);
 *   const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);
 *
 *   // 4. Use isOpen (not context.open) in render
 *   return (
 *     <>
 *       <div ref={refs.setReference} {...getReferenceProps()}>Trigger</div>
 *       {isOpen && <FloatingPortal>...</FloatingPortal>}
 *     </>
 *   );
 * };
 * ```
 */
export const FLOATING_UI_COMPONENT_TEMPLATE = `
const MyTooltip = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(5), flip(), shift()],
  });
  
  const hover = useHover(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);
  
  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()}>Trigger</div>
      {isOpen && <FloatingPortal>...</FloatingPortal>}
    </>
  );
};
`;

/**
 * Common patterns for different floating UI use cases
 */
export const FLOATING_UI_PATTERNS = {
  /** Standard tooltip pattern */
  TOOLTIP: {
    middleware: ['offset(5)', 'flip()', 'shift({ padding: 8 })'],
    interactions: ['useHover', 'useDismiss', 'useRole'],
    role: 'tooltip',
    delayDefault: 0,
  },

  /** Overflow tooltip pattern */
  OVERFLOW_TOOLTIP: {
    middleware: ['offset(9)', 'flip()', 'shift({ padding: 8 })'],
    interactions: ['useHover', 'useDismiss', 'useRole'],
    role: 'tooltip',
    delayDefault: 250,
  },

  /** Context menu pattern */
  CONTEXT_MENU: {
    middleware: ['autoPlacement({ alignment: "start" })'],
    interactions: ['useRole', 'useDismiss'],
    role: 'menu',
    trigger: 'contextmenu',
  },
} as const;
